"use client";
import { WeekSwitch } from "@cmp-src/components/calendar/WeekSwitch";
import { DashboardContentWrapper } from "@cmp-src/components/dashboard/DashboardContentWrapper";
import {
  CardV2LayoutNoShadowClasses,
  MobileBottomCalcVh
} from "@cmp-src/data-display/Card/classes";
import { SelectOffices } from "@cmp-src/form/OfficeSelectorInput/SelectOffices";
import { Switch } from "@cmp-src/primitives/Switch";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent
} from "@cmp-src/primitives/sidebar";
import { Tabs, TabsContent } from "@cmp-src/primitives/tabs";
import { ColumnWidthProvider } from "@gettactic/components/src/contexts/ColumnWidthContext";
import { NotificationsList } from "@gettactic/components/src/data-display/NotificationBanner";
import { cn, getDefaultDashboardTab } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import {
  useAuthenticated,
  useHasStations,
  useSettings
} from "@gettactic/hooks";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { CalendarRange } from "lucide-react";
import { useMemo } from "react";
import { AgendaWeek } from "@/features/dashboard/components/AgendaWeek";
import { OutOfOfficeRequest } from "@/features/dashboard/components/attendance/employee/OutOfOfficeRequest";
import { DashboardDedicatedDeskDetails } from "@/features/dashboard/components/DashboardDedicatedDeskDetails";
import { DashboardDetailsWrapper } from "@/features/dashboard/components/DashboardDetailsWrapper";
import { DashboardEventDetails } from "@/features/dashboard/components/DashboardEventDetails/DashboardEventDetails";
import { DashboardVisitDetails } from "@/features/dashboard/components/DashboardVisitDetails";
import { LocationChangeReservationsModal } from "@/features/dashboard/components/LocationChangeReservationsModal";
import { DashboardTabsList } from "@/features/dashboard/components/partial/DashboardTabsList";
import { LiveMap } from "@/features/dashboard/components/subpages/LiveMap";
import { News } from "@/features/dashboard/components/subpages/News";
import { SiteInfo } from "@/features/dashboard/components/subpages/SiteInfo";
import { Visitors } from "@/features/dashboard/components/subpages/Visitors";
import type { PageOptions } from "@/features/dashboard/DashboardStateContext";
import { pageOptionToTab } from "@/features/dashboard/defines";
import { useDashboardData } from "@/features/dashboard/hooks/useDashboardData";
import { isDedicatedDeskSelection } from "@/features/dashboard/lib/dedicatedDeskSelection";
import { TeamSchedule } from "@/features/teams/components/TeamSchedule";
import { scheduleToOfficeWallClock } from "@/lib/scheduleToOfficeWallClock";

// Type guard to distinguish Visit from AgendaSchedule in selectedEvent
const isVisit = (event: unknown): boolean => {
  return !!event && typeof event === "object" && "visitor" in event;
};

export const Dashboard = () => {
  const { hasStations } = useHasStations();
  const { isBreakpoint } = useBreakpoint();

  const { isOfficePrivacyEnabled } = useSettings();
  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();
  const isOrgPrivacyEnabled = isOfficePrivacyEnabled(currentOffice?.id);

  const defaultTab: PageOptions = useMemo(
    () =>
      getDefaultDashboardTab(
        isOrgPrivacyEnabled,
        false,
        authenticatedUser.slug
      ),
    [isOrgPrivacyEnabled, authenticatedUser.slug]
  );

  const isMd = isBreakpoint("md");

  const {
    shouldRenderContent,
    selectedEvent,
    setSelectedEvent,
    containerRef,
    agendaDisclosure,
    isAdmin,
    hasPages,
    weekDate,
    setWeekDate,
    isComponentLoading,
    nextWeek,
    prevWeek,
    scheduleMeData,
    oooRequest,
    setOooRequest,
    dashboardFilters,
    pendingLocationChange,
    dismissPendingLocationChange,
    setOffices,
    dashboardWidth,
    handleTabChange,
    activeTab,
    WHOLE_WIDTH,
    locationData,
    finalDaysCount,
    setPage
  } = useDashboardData({ defaultTab });

  // The agenda endpoint returns true-UTC times, while DashboardEventDetails
  // reads the office wall-clock "fake Z" convention of the schedules
  // endpoints — the same normalisation AgendaDay's edit flow does. Without it
  // the details header shows the hour, and for late-evening reservations the
  // day, shifted by the office offset until the by-id refetch lands [sc-9350].
  const selectedSchedule = useMemo(
    () =>
      selectedEvent &&
      !isVisit(selectedEvent) &&
      !isDedicatedDeskSelection(selectedEvent)
        ? scheduleToOfficeWallClock(
            selectedEvent,
            selectedEvent.office?.time_zone
          )
        : null,
    [selectedEvent]
  );

  if (!shouldRenderContent) {
    return null; // Return null until router is ready to prevent hooks from being conditionally rendered
  }

  const calendarSwitch = (
    <button
      type="button"
      className={"flex items-center gap-1 md:hidden"}
      onClick={() => {
        agendaDisclosure?.toggle();
      }}
    >
      <CalendarRange className={"size-4"} />
      <Switch
        checked={agendaDisclosure?.isOpen || false}
        variant={"squared"}
        size={"xs"}
      />
    </button>
  );

  // Always render both components, but hide one with CSS
  return (
    <>
      <div className={"relative h-full"}>
        <div className={cn("flex h-full flex-row")}>
          <div
            data-state={agendaDisclosure?.isOpen ? "open" : "closed"}
            className={cn(
              agendaDisclosure?.isOpen ? "fixed" : "hidden",
              "top-12 right-4 bottom-0 left-0 z-sticky flex-col bg-white md:static md:flex md:bg-transparent",
              !isMd &&
                "data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2/3 data-[state=open]:fade-in-0 data-[state=open]:animate-in",
              !isMd &&
                "data-[state=closed]:zoom-out-90 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out"
            )}
            style={isMd ? { width: WHOLE_WIDTH } : undefined}
          >
            <div className="flex w-full items-center justify-between gap-2 pt-v2-layout-card-out pl-v2-layout-card-out text-sm">
              {calendarSwitch}
              <WeekSwitch
                prevWeek={prevWeek}
                nextWeek={nextWeek}
                setWeekDate={setWeekDate}
                weekDate={`${weekDate}T00:00:00.000Z`}
              />
            </div>
            <ScrollArea.Root
              type="hover"
              className={cn(
                "pt-6 pl-v2-layout-card-out md:h-[calc(100vh-4rem)] "
              )}
            >
              <ScrollArea.Viewport
                className={`${MobileBottomCalcVh} [&>div]:!block w-full md:h-full`}
              >
                <SidebarContent className=" ">
                  <SidebarGroup className="px-0">
                    <SidebarGroupContent className="">
                      {scheduleMeData.data ? (
                        <AgendaWeek locationData={locationData} />
                      ) : null}
                    </SidebarGroupContent>
                  </SidebarGroup>
                </SidebarContent>
              </ScrollArea.Viewport>
              <Scrollbar className="-mr-v2-layout-card-scrollbar" />
            </ScrollArea.Root>
          </div>
          <div
            className={cn(
              "relative flex flex-1 flex-col overflow-hidden",
              `${CardV2LayoutNoShadowClasses} w-full transition-[width] duration-300 ease-in-out`,
              "ml-0 md:m-v2-layout-card-out"
            )}
          >
            <ColumnWidthProvider
              daysCount={finalDaysCount}
              containerRef={containerRef}
            >
              <Tabs
                defaultValue={pageOptionToTab[defaultTab]}
                value={activeTab}
                onValueChange={(value) => {
                  handleTabChange(value);
                }}
                className="w-full items-center justify-center"
              >
                <div className="flex w-full flex-col items-center justify-center">
                  <DashboardContentWrapper maxWidth={dashboardWidth}>
                    <div
                      className="flex w-full flex-row items-center justify-between gap-6 md:gap-0"
                      ref={containerRef}
                    >
                      {calendarSwitch ? (
                        <div className={"mt-7 mb-4"}>{calendarSwitch}</div>
                      ) : null}

                      <DashboardTabsList
                        hasPages={hasPages}
                        selectedEvent={selectedEvent}
                        setSelectedEvent={setSelectedEvent}
                        showAttendanceTab={!isOrgPrivacyEnabled}
                        setPage={setPage}
                        defaultPage={defaultTab}
                      />
                      {!selectedEvent && (
                        <div className={"mt-3 md:mt-0"}>
                          <SelectOffices
                            multiselect
                            splitView
                            contentWidth="480px"
                            offices={dashboardFilters.offices}
                            onChange={(ids: string[]) => {
                              setOffices(ids);
                            }}
                            disabled={isComponentLoading}
                            showSelectedLabel={isMd}
                            allowClear={false}
                            variant="ghost"
                          />
                        </div>
                      )}
                    </div>
                  </DashboardContentWrapper>
                </div>

                <ScrollArea.Root
                  type="hover"
                  scrollHideDelay={500}
                  className={`flex-1 ${MobileBottomCalcVh} md:h-[calc(100vh-10rem)]`}
                >
                  <ScrollArea.Viewport className="[&>div]:!block block h-full w-full">
                    <div className="flex w-full flex-col items-center justify-center">
                      <DashboardContentWrapper maxWidth={dashboardWidth}>
                        <NotificationsList />
                        <TabsContent value="schedules" asChild>
                          <div>
                            {selectedEvent && isVisit(selectedEvent) ? (
                              <DashboardDetailsWrapper
                                title="Visitor Details"
                                onBack={() => setSelectedEvent(null)}
                              >
                                <DashboardVisitDetails visit={selectedEvent} />
                              </DashboardDetailsWrapper>
                            ) : isDedicatedDeskSelection(selectedEvent) ? (
                              <DashboardDetailsWrapper
                                title=""
                                onBack={() => setSelectedEvent(null)}
                              >
                                <DashboardDedicatedDeskDetails
                                  desk={selectedEvent.desk}
                                  date={selectedEvent.date}
                                />
                              </DashboardDetailsWrapper>
                            ) : selectedSchedule ? (
                              <DashboardDetailsWrapper
                                title=""
                                onBack={() => setSelectedEvent(null)}
                              >
                                <DashboardEventDetails
                                  schedule={selectedSchedule}
                                  isOrgPrivacyEnabled={isOrgPrivacyEnabled}
                                  calendarSwitch={
                                    <div className="flex w-full items-center justify-between gap-2">
                                      {calendarSwitch}
                                    </div>
                                  }
                                />
                              </DashboardDetailsWrapper>
                            ) : isOrgPrivacyEnabled ? (
                              <SiteInfo />
                            ) : (
                              <TeamSchedule daysCount={finalDaysCount} />
                            )}
                          </div>
                        </TabsContent>

                        <TabsContent value="liveMap" asChild>
                          <div>
                            <LiveMap />
                          </div>
                        </TabsContent>

                        <TabsContent value="visitors" asChild>
                          <div>
                            <Visitors />
                          </div>
                        </TabsContent>

                        <TabsContent value="news" asChild>
                          <div>
                            <News />
                          </div>
                        </TabsContent>
                        <TabsContent value="siteInfo" asChild>
                          <div>
                            <SiteInfo />
                          </div>
                        </TabsContent>
                      </DashboardContentWrapper>
                    </div>
                  </ScrollArea.Viewport>
                  <Scrollbar className="z-50 mr-v2-layout-card-scrollbar" />
                </ScrollArea.Root>
              </Tabs>
            </ColumnWidthProvider>
          </div>
        </div>
      </div>
      <OutOfOfficeRequest
        isOpen={!!oooRequest}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setOooRequest(null);
            locationData.invalidateQueries();
          }
        }}
        request={oooRequest}
      />
      <LocationChangeReservationsModal
        pending={pendingLocationChange}
        onClose={dismissPendingLocationChange}
      />
    </>
  );
};

const Scrollbar = ({ className }: { className?: string }) => {
  return (
    <ScrollArea.Scrollbar
      orientation="vertical"
      className={cn(
        "z-50 flex w-3 bg-neutral-50/50 p-[2px] hover:bg-neutral-100",
        className
      )}
    >
      <ScrollArea.Thumb className="relative flex-1 rounded-full bg-neutral-400 hover:bg-neutral-500" />
    </ScrollArea.Scrollbar>
  );
};
